import { useEffect, useState, type KeyboardEvent, type ReactElement } from 'react';
import { ActionIcon, Box, Group, Popover, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, Sparkles, X } from 'lucide-react';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import type { AssistContext } from '@/shared/assist/AssistContract';
import { useGlobalFilters } from '@/features/global-filters/useGlobalFilters';
import { resolveDateRange } from '@/features/global-filters/GlobalFilterTypes';
import type { AnalyticsSurface } from '../AnalyticsSurface';
import type { AnalyticsField } from '@/shared/analytics/AnalyticsTypes';
import { useAssistFilters, useAssistSurface, useAssistViewModel } from './AssistSurfaceProvider';
import SurfaceAssistResultPanel from './SurfaceAssistResultPanel';

const INSPIRATION: string[] = [
    'Top departments by total tokens',
    'Model mix across the org',
    'Only the Engineering department',
    'Usage over time',
    'Which prompts contain PII?',
];

const PANEL_WIDTH = 420;

interface SurfaceAssistBarProps {
    surface: AnalyticsSurface;
}

/**
 * The prominent "Ask me anything" assist control. Focusing it floats inspiration
 * prompts; submitting opens the response (narrative + interactive chart) in the
 * dropdown below the input, where it can be pinned to the page as a tile. The
 * assist has no persistent chip of its own — its effects show up in the page's
 * content (filters land in the filter row, charts pin as tiles). The input
 * carries an inline clear and the dropdown an explicit hide control; the last
 * response persists across reloads (rehydrated on mount) and reopens on focus.
 */
/** UTC `YYYY-MM-DD HH:MM:SS` — the timestamp-literal format the assist's DuckDB queries inline. */
function sqlTimestamp(date: Date): string {
    return date.toISOString().slice(0, 19).replace('T', ' ');
}

export default function SurfaceAssistBar({ surface }: SurfaceAssistBarProps): ReactElement {
    const { topicId } = useParams<{ topicId: string }>();
    const navigate = useNavigate();
    const vm = useAssistViewModel();
    const controller = useAssistSurface();
    const activeFilters = useAssistFilters();
    const { snapshot: globalSnapshot } = useGlobalFilters();
    const [open, setOpen] = useState(false);
    const isWide = useBreakpointUp('sm');
    const isRoomy = useBreakpointUp('md');
    const inputWidth = !isWide ? '100%' : isRoomy ? 320 : 240;

    const buildContext = (): AssistContext => {
        const topic = topicId === undefined ? undefined : surface.catalog.find((entry) => entry.id === topicId);
        const { start, end } = resolveDateRange(globalSnapshot.dateRange);
        return {
            surfaceId: surface.id,
            surfaceTitle: surface.title,
            topicId,
            surfaceScope: surface.assistScope,
            topicFocus: topic === undefined ? undefined : `${topic.title} — ${topic.question}`,
            dateWindow: { start: sqlTimestamp(start), end: sqlTimestamp(end) },
            activeFilters,
        };
    };

    useEffect(() => {
        if (vm !== null && vm.needsRehydrate) void vm.rehydrate(buildContext());
        // eslint-disable-next-line react-hooks/exhaustive-deps -- rehydrate self-guards; runs on mount / surface / topic change only (live filters read at call time)
    }, [vm, surface.id, topicId]);

    const submit = async (text?: string): Promise<void> => {
        if (vm === null) return;
        if (text !== undefined) vm.setPrompt(text);
        setOpen(true);
        await vm.ask(buildContext());
        const plan = vm.plan;
        if (plan !== null && plan.workflow === 'filter' && controller !== null) {
            controller.addFilters(plan.filters);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
            void submit();
        }
    };

    const handleDrill = (field: AnalyticsField, value: string): void => {
        controller?.addFilter(field, value);
    };

    /** Closing the popover disposes the held result and prompt (and the persisted session). */
    const handleOpenChange = (next: boolean): void => {
        setOpen(next);
        if (!next) vm?.reset();
    };

    const hasResult = vm !== null && (vm.loading || vm.error !== null || vm.plan !== null);

    return (
        <Group gap="xs" wrap="nowrap" align="center" style={{ flex: isWide ? undefined : 1, minWidth: 0 }}>
            <Popover opened={open} onChange={handleOpenChange} position="bottom-end" width={PANEL_WIDTH} shadow="md" radius="md" closeOnClickOutside trapFocus={false} withinPortal>
                <Popover.Target>
                    <TextInput
                        value={vm?.promptText ?? ''}
                        onChange={(event) => vm?.setPrompt(event.currentTarget.value)}
                        onFocus={() => setOpen(true)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask me anything"
                        leftSection={<Sparkles size={16} />}
                        rightSectionPointerEvents={vm?.hasContent ? 'all' : 'none'}
                        rightSection={
                            vm?.hasContent ?
                                <Tooltip label="Clear" withArrow>
                                    <ActionIcon variant="subtle" color="gray" size="sm" radius="xl" aria-label="Clear" onClick={() => vm.reset()}>
                                        <X size={14} />
                                    </ActionIcon>
                                </Tooltip>
                            :   null
                        }
                        size="sm"
                        radius="md"
                        w={inputWidth}
                        aria-label="Ask me anything"
                    />
                </Popover.Target>
                <Popover.Dropdown>
                    <Group justify="space-between" align="center" mb="xs" wrap="nowrap">
                        <Group gap={6} wrap="nowrap" c="dimmed" style={{ minWidth: 0 }}>
                            <Sparkles size={13} />
                            <Text size="xs" fw={700} tt="uppercase" truncate style={{ letterSpacing: '0.06em' }}>
                                Ask me anything
                            </Text>
                        </Group>
                        <Tooltip label="Hide" withArrow>
                            <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Hide" onClick={() => handleOpenChange(false)} style={{ flexShrink: 0 }}>
                                <X size={15} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                    {hasResult && vm !== null ?
                        <SurfaceAssistResultPanel
                            vm={vm}
                            surfaceId={surface.id}
                            onDrill={handleDrill}
                            pinTarget={surface.drillStyle === 'side-rail' ? 'nav' : 'page'}
                            onPinned={(tileId) => {
                                handleOpenChange(false);
                                navigate(`${surface.basePath}/${tileId}`);
                            }}
                        />
                    :   <InspirationPrompts prompts={surface.assistInspiration ?? INSPIRATION} onPick={(text) => void submit(text)} />}
                </Popover.Dropdown>
            </Popover>
        </Group>
    );
}

function InspirationPrompts({ prompts, onPick }: { prompts: string[]; onPick: (text: string) => void }): ReactElement {
    return (
        <Box>
            <Group gap={5} mb="xs" wrap="nowrap" c="dimmed">
                <Search size={13} />
                <Text size="xs" fw={700} tt="uppercase" style={{ letterSpacing: '0.06em' }}>
                    Try
                </Text>
            </Group>
            <Stack gap={2}>
                {prompts.map((prompt) => (
                    <Box
                        key={prompt}
                        component="button"
                        type="button"
                        onClick={() => onPick(prompt)}
                        className="topic-drill-row"
                        style={{ textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', padding: '7px 8px', borderRadius: 6, font: 'inherit', width: '100%' }}
                    >
                        <Text size="sm">{prompt}</Text>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}
